/**
 * KAN-980 / KAN-979: хуки раздела "Платформа" поверх services/api/platform.ts.
 * Мутации обновляют список кабинетов (баланс, число аккаунтов) и пул.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adjustPlatformWallet,
  assignPlatformTgAccounts,
  clearTgFloodMarks,
  getPlatformOrganizationOperators,
  getPlatformOrganizations,
  getPlatformPricing,
  getPlatformTgPool,
  returnPlatformTgAccounts,
  updatePlatformPricing,
  type AssignTgAccountsPayload,
  type PlatformPricingPatch,
  type ReturnTgAccountsPayload,
  type TgPoolScope,
  type WalletAdjustmentPayload,
} from '@/services/api/platform';

export const platformKeys = {
  organizations: ['platform', 'organizations'] as const,
  operators: (orgId: string) => ['platform', 'organizations', orgId, 'operators'] as const,
  tgPoolAll: ['platform', 'tg-pool'] as const,
  tgPool: (scope: TgPoolScope) => ['platform', 'tg-pool', scope] as const,
  pricing: ['platform', 'pricing'] as const,
};

export function usePlatformOrganizations() {
  return useQuery({ queryKey: platformKeys.organizations, queryFn: getPlatformOrganizations });
}

export function usePlatformOperators(orgId: string | null) {
  return useQuery({
    queryKey: platformKeys.operators(orgId ?? ''),
    queryFn: () => getPlatformOrganizationOperators(orgId as string),
    enabled: Boolean(orgId),
  });
}

export function usePlatformTgPool(scope: TgPoolScope) {
  return useQuery({ queryKey: platformKeys.tgPool(scope), queryFn: () => getPlatformTgPool(scope) });
}

export function useAdjustPlatformWallet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, payload }: { orgId: string; payload: WalletAdjustmentPayload }) =>
      adjustPlatformWallet(orgId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: platformKeys.organizations }),
  });
}

function useInvalidateAfterTransfer() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: platformKeys.tgPoolAll }),
      queryClient.invalidateQueries({ queryKey: platformKeys.organizations }),
    ]);
}

export function useAssignPlatformTgAccounts() {
  const invalidate = useInvalidateAfterTransfer();
  return useMutation({
    mutationFn: (payload: AssignTgAccountsPayload) => assignPlatformTgAccounts(payload),
    onSuccess: invalidate,
  });
}

export function useReturnPlatformTgAccounts() {
  const invalidate = useInvalidateAfterTransfer();
  return useMutation({
    mutationFn: (payload: ReturnTgAccountsPayload) => returnPlatformTgAccounts(payload),
    onSuccess: invalidate,
  });
}

/** KAN-997: снять пометку "Был под флудом"; затем перезапросить пул и кабинеты (счётчики). */
export function useClearTgFloodMarks() {
  const invalidate = useInvalidateAfterTransfer();
  return useMutation({
    mutationFn: (accountIds: string[]) => clearTgFloodMarks(accountIds),
    onSuccess: invalidate,
  });
}

/** KAN-1000: цены платформы (курс и наценки). */
export function usePlatformPricing() {
  return useQuery({ queryKey: platformKeys.pricing, queryFn: getPlatformPricing });
}

export function useUpdatePlatformPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: PlatformPricingPatch) => updatePlatformPricing(patch),
    onSuccess: (data) => queryClient.setQueryData(platformKeys.pricing, data),
  });
}
